/**
 * Tile — a single letter tile in the word grid.
 * Handles flip animation and color states.
 */
import React, { useState, useEffect } from 'react';
import { TILE_STATE } from '../../utils/constants';

const stateColors = {
  [TILE_STATE.EMPTY]: 'border-gray-300 dark:border-gray-600',
  [TILE_STATE.TBD]: 'border-gray-500 dark:border-gray-400',
  [TILE_STATE.CORRECT]: 'bg-correct border-correct text-white',
  [TILE_STATE.PRESENT]: 'bg-present border-present text-white',
  [TILE_STATE.ABSENT]: 'bg-absent dark:bg-absent-dark border-absent dark:border-absent-dark text-white',
};

const Tile = ({ letter, state, delay: animDelay = 0, shouldBounce = false, bounceDelay = 0 }) => {
  const [animState, setAnimState] = useState('idle');
  const [displayState, setDisplayState] = useState(TILE_STATE.EMPTY);

  useEffect(() => {
    if (state && state !== TILE_STATE.EMPTY && state !== TILE_STATE.TBD) {
      // Start flip-in animation after delay
      const flipInTimer = setTimeout(() => {
        setAnimState('flip-in');
      }, animDelay);

      // At midpoint of flip, change color
      const colorTimer = setTimeout(() => {
        setDisplayState(state);
        setAnimState('flip-out');
      }, animDelay + 300);

      // Animation complete
      const doneTimer = setTimeout(() => {
        setAnimState('done');
      }, animDelay + 600);

      return () => {
        clearTimeout(flipInTimer);
        clearTimeout(colorTimer);
        clearTimeout(doneTimer);
      };
    } else {
      setDisplayState(state || TILE_STATE.EMPTY);
      setAnimState('idle');
    }
  }, [state, animDelay]);

  // Pop animation when a letter is typed
  const popClass = letter && (!state || state === TILE_STATE.TBD) ? 'tile-pop' : '';

  // Bounce on win
  const [bouncing, setBouncing] = useState(false);
  useEffect(() => {
    if (shouldBounce) {
      const timer = setTimeout(() => setBouncing(true), bounceDelay);
      return () => clearTimeout(timer);
    }
  }, [shouldBounce, bounceDelay]);

  const flipClass =
    animState === 'flip-in'
      ? 'tile-flip-in'
      : animState === 'flip-out'
      ? 'tile-flip-out'
      : '';

  const bounceClass = bouncing ? 'tile-bounce' : '';

  const colorClass = stateColors[displayState] || stateColors[TILE_STATE.EMPTY];

  return (
    <div className="perspective-500">
      <div
        className={`
          w-14 h-14 sm:w-16 sm:h-16
          border-2 flex items-center justify-center
          text-2xl sm:text-3xl font-bold uppercase
          select-none transition-colors
          ${colorClass}
          ${popClass}
          ${flipClass}
          ${bounceClass}
        `}
      >
        {letter || ''}
      </div>
    </div>
  );
};

export default React.memo(Tile);
